import React from 'react';
import { useTranslation } from 'react-i18next';
import { money } from '../../utils/region';

// ── A receipt's withholding, asked of an entry recorded BY HAND for its statement line (W3.1; W3.2; batch 11 W4) ──────────
// ONE copy of the screen's half of the hand door's WHT-prepaid rule (server: bankSettlement.handPrepaidLock /
// handPrepaidRefusal), read by the two screens that write such an entry: Record by hand (ManualEntry, New) and the draft
// editor (DraftEditDialog, the PUT — batch 12 tail, the review: it sent a PUT certain to be refused, never having asked).
// Each reads GET /journal/hand-door first — the editor with exceptEntryId, its own claim excepted — and holds Save while
// prepaidHoldOf finds a hit, saying why with PrepaidSentence. The door's 409 is never sent from either.

/** What GET /journal/hand-door answers as `prepaid`: the WHT-prepaid accounts the entry may not debit (or not above
 *  `roomSat`), the case and the figures the sentence is worded with. */
export type HandDoorLock = { case: string; message: string; params: Record<string, string>; accountIds: string[]; roomSat?: number; bookedSat?: number; withheldSat?: number };
/** The lines' WHT-prepaid debits against the lock: how many lines debit a locked account (`debits`), their sum (`mineSat`),
 *  the accounts held (`hit` — empty when Save may go), and the sentence's case (`caseKey`: 'over' for a roomed case). */
export type PrepaidHold = { debits: number; mineSat: number; hit: string[]; caseKey: string };

/** The WHT-prepaid debits of an entry the hand door would refuse, by account: any, where nothing of the withholding is left
 *  for this line ('taken', and whose-cannot-be-told); for 'part' — and 'whole', nothing else carrying it (batch 11 W4, the
 *  gate review) — more than what is left (then 'over', the door's own word for it). `lines` in satang. */
export function prepaidHoldOf(lock: HandDoorLock | null | undefined, lines: { accountId: string; drSat: number }[]): PrepaidHold {
  if (!lock) return { debits: 0, mineSat: 0, hit: [], caseKey: '' };
  const mine = lines.filter(l => l.drSat > 0 && lock.accountIds.includes(l.accountId));
  const mineSat = mine.reduce((s, l) => s + l.drSat, 0);
  const roomed = lock.case === 'part' || lock.case === 'whole';
  const hit = mine.length && (!roomed || mineSat > Number(lock.roomSat || 0)) ? Array.from(new Set(mine.map(l => l.accountId))) : [];
  return { debits: mine.length, mineSat, hit, caseKey: roomed ? 'over' : lock.case };
}

/** Why Save is held, in the reader's language, with the figures (the server's own sentence is the caller's hover). */
export default function PrepaidSentence({ lock, hold, codeOf }: { lock: HandDoorLock; hold: PrepaidHold; codeOf: (accountId: string) => string }) {
  const { t } = useTranslation();
  const baht = (s: number) => money(Number(s || 0) / 100);
  const p = lock.params || {};
  const codes = hold.hit.map(codeOf).filter(Boolean).join(', ') || '—';
  let words: string;
  if (lock.case === 'unclear_self') words = t('pa_manual_prepaid_unclear_self', { invoice: p.invoice || '—', receipts: p.receipts || '—', codes });
  else if (lock.case === 'unclear_other') words = t('pa_manual_prepaid_unclear_other', { docNo: p.byNo || '—', line: p.line || '—', invoice: p.invoice || '—', receipt: p.receipt || '—', codes });
  else if (lock.case === 'whole') {
    words = t('pa_manual_prepaid_over_whole', { receipt: p.receipt || '—', withheld: baht(Number(lock.withheldSat || 0)), mine: baht(hold.mineSat), room: baht(Number(lock.roomSat || 0)), codes });
  } else {
    const by1 = p.byKind === 'hand' ? t('pa_manual_prepaid_by_hand', { docNo: p.byNo || '—', line: p.line || '—' }) : t('pa_manual_prepaid_by_settlement', { line: p.line || '—' });
    const by = Number(p.more) > 0 ? t('pa_manual_prepaid_by_more', { by: by1, n: p.more }) : by1;
    if (lock.case === 'part') {
      words = t('pa_manual_prepaid_over', { receipt: p.receipt || '—', withheld: baht(Number(lock.withheldSat || 0)), booked: baht(Number(lock.bookedSat || 0)),
        by, mine: baht(hold.mineSat), room: baht(Number(lock.roomSat || 0)), codes });
    } else {
      const way = p.way === 'closed' ? t('pa_manual_prepaid_way_closed', { codes })
        : p.way === 'several' ? t('pa_manual_prepaid_way_several', { codes })
          : p.way === 'post_reversal' ? t('pa_manual_prepaid_way_post_reversal', { codes, rv: p.rev || '—' })
            : p.way === 'reverse' ? t('pa_manual_prepaid_way_reverse', { codes }) : t('pa_manual_prepaid_way_discard', { codes });
      words = t('pa_manual_prepaid_taken', { receipt: p.receipt || '—', by, way });
    }
  }
  return <>{words}</>;
}
